import {
  PriceBookDetails,
  PriceBookHeader,
  PriceBookSpendGoals,
  KeyCategories,
  TemporaryPlacementMessage,
} from '@thd-olt-component-react/ocm_pd_component_myaccount-pricebook';
import React, { useEffect, useRef } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { IPriceBookError } from '@thd-olt-component-react/ocm_pd_component_myaccount-pricebook/dist/types/errors';
import { getChannelID } from '../../utils/NetworkUtil';
import { getSvocId, getUserId } from '../../utils/user';
import { NationalAccount } from '../../components/PricingBenefits';
import { LegacyTiersProvider, RewardsAndOffersProvider } from '../../contexts';
import { useIsMobile, useRewardsAndOffers } from '../../hooks';

import { ERR_PRICING_BOOK } from '../../constants';
import { newrelic, PRICING_BOOK_PAGE_LOAD } from '../../utils/newrelic';

export interface PricingBookContainerProps {
  temporaryEnrollment: boolean;
  showV2PriceBook: boolean;
}

export const PricingBookContainer: React.FunctionComponent<
  PricingBookContainerProps
> = ({ temporaryEnrollment, showV2PriceBook }) => {
  const { isMobile } = useIsMobile();
  const { rewardsAndOffers, generalError, setGeneralError } =
    useRewardsAndOffers();
  const showPackageSwitchLink = !!useConfigService('showPackageSwitchLink');

  const errorCalledRef = useRef(false);

  const channelId = getChannelID();
  const userId = getUserId() ?? '';
  const customerAccountId = getSvocId() ?? '';

  const getVersion = () => {
    return temporaryEnrollment ? 'tempbook' : 'v1';
  };

  const handlePricingBookError = (error: IPriceBookError) => {
    errorCalledRef.current = true;
    newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
      result: 'error',
      errorMessage: error?.message || 'component error',
      version: getVersion(),
    });
    if (generalError === null) {
      setGeneralError(ERR_PRICING_BOOK);
    }
  };

  useEffect(() => {
    // If no error was reported after initial render, log success
    if (!errorCalledRef.current && !generalError) {
      newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
        result: 'success',
        version: getVersion(),
      });
    }
  }, [generalError]);

  return (
    <div>
      {!generalError ? (
        <div className="sui-gap-8 sui-flex sui-flex-col">
          <PriceBookHeader
            userId={userId}
            customerAccountId={customerAccountId}
            channelId={channelId}
            onError={handlePricingBookError}
            returnNullIfError
            disableAnalytics={false}
          />
          <div className="sui-flex sui-flex-col md:sui-flex-row sui-gap-4 sui-justify-between md:sui-items-stretch">
            {!temporaryEnrollment ? (
              <>
                <div className="sui-w-full sui-flex sui-flex-col md:sui-w-1/2">
                  <PriceBookSpendGoals
                    userId={userId}
                    customerAccountId={customerAccountId}
                    channelId={channelId}
                    onError={handlePricingBookError}
                    returnNullIfError
                    disableAnalytics={false}
                  />
                </div>

                <div
                  className="sui-w-full md:sui-w-1/2 sui-flex sui-flex-col"
                  data-testid="selected-package-column"
                >
                  <PriceBookDetails
                    userId={userId}
                    customerAccountId={customerAccountId}
                    channelId={channelId}
                    onError={handlePricingBookError}
                    returnNullIfError
                    showPackageSwitchLink={showPackageSwitchLink}
                  />
                </div>
              </>
            ) : (
              <div className="sui-w-full sui-flex sui-flex-col">
                <TemporaryPlacementMessage
                  userId={userId}
                  customerAccountId={customerAccountId}
                  channelId={channelId}
                  onError={handlePricingBookError}
                  returnNullIfError
                  showV2PriceBook={showV2PriceBook}
                />
              </div>
            )}
          </div>
          <div className="sui-w-full">
            <KeyCategories
              userId={userId}
              customerAccountId={customerAccountId}
              channelId={channelId}
              onError={handlePricingBookError}
              returnNullIfError
            />
          </div>
          <span className="sui-h-5 sui-border-solid sui-border-primary sui-border-b-1" />
          <RewardsAndOffersProvider>
            <LegacyTiersProvider rewardsAndOffers={rewardsAndOffers}>
              <NationalAccount
                isMobile={isMobile}
                nationalAccount={
                  rewardsAndOffers?.customerInfo?.nationalAccountDetails
                }
              />
            </LegacyTiersProvider>
          </RewardsAndOffersProvider>
        </div>
      ) : null}
    </div>
  );
};
